import { Button, Form, Row, Col, Descriptions, Table, DatePicker, Flex, message } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { ColumnsType } from 'antd/es/table';
import { atom } from 'nanostores';
import { useStore } from '@nanostores/react';
import { useEffect } from 'react';
import { User, Scheduler, Config, ConfigItem, PDItem } from '../../api/go';
import { DateTime } from 'luxon';
import { escapeRegExp } from 'es-toolkit/string';

enum AutoBanType {
  HRNA_UUID_OS = 'hrna_uuid_os',
  HRNA_UUID_HARDWARE = 'hrna_uuid_hardware',
  PROCESS_KEYWORD = 'process_keyword',
  MAP_REPORT = 'map_report',
  PROCESS_V2 = 'process_report_v2',
}

interface ProcessReportV2Data {
  type: 'process_report_v2';
  game_ids: string[];
  report_window: {
    start: string;
    end: string;
  };
  detect_window: {
    start: string;
    end: string;
  };
  fetched_log_count: number;
  matched_category_count: number;
  categories: {
    key: string;
    name: string;
    matched_log_ids: number[];
  }[];
}

interface HrnaAutoBanData {
  uuid: string;
  related_banned_user_ids: string[];
  related_banned_count: number;
}

interface RelatedUserInfo {
  userId: string;
  nick: string;
  status: number;
}

function isHrnaLog(type: string) {
  return type === AutoBanType.HRNA_UUID_HARDWARE || type === AutoBanType.HRNA_UUID_OS;
}

function parseHrnaAutoBanData(data: string): HrnaAutoBanData | null {
  try {
    const parsed = JSON.parse(data) as Partial<HrnaAutoBanData>;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (typeof parsed.uuid !== 'string' || !Array.isArray(parsed.related_banned_user_ids)) {
      return null;
    }

    const relatedUserIds = parsed.related_banned_user_ids.filter((userId): userId is string => typeof userId === 'string' && userId !== '');
    return {
      uuid: parsed.uuid,
      related_banned_user_ids: relatedUserIds,
      related_banned_count: typeof parsed.related_banned_count === 'number' ? parsed.related_banned_count : relatedUserIds.length,
    };
  } catch {
    return null;
  }
}

function getAccountStatusText(value: number) {
  switch (value) {
    case 0:
      return '封禁';
    case 1:
      return '正常';
    case 2:
    default:
      return '未知';
  }
}

function renderAccountStatus(value: number) {
  return <div style={value === 0 ? { color: 'red', fontWeight: 'bold' } : {}}>{getAccountStatusText(value)}</div>;
}

function countUniqueUsers(logList: LogItemEx[], predicate: (log: LogItemEx) => boolean) {
  return new Set(logList.filter(predicate).map((log) => log.user_id)).size;
}

const columns: ColumnsType<LogItemEx> = [
  {
    width: 280,
    title: '处罚时间',
    dataIndex: 'date',
    key: 'date',
    render: (value) => {
      return DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm:ss');
    },
  },
  {
    width: 220,
    title: '类型',
    dataIndex: 'type',
    key: 'type',
    filters: [
      {
        text: '高风险新账号-系统关联',
        value: AutoBanType.HRNA_UUID_OS,
      },
      {
        text: '高风险新账号-硬件关联',
        value: AutoBanType.HRNA_UUID_HARDWARE,
      },
      {
        text: '进程信息-关键词',
        value: AutoBanType.PROCESS_KEYWORD,
      },
      {
        text: '地图上报',
        value: AutoBanType.MAP_REPORT,
      },
      {
        text: '进程扫描V2',
        value: AutoBanType.PROCESS_V2,
      },
      {
        text: '其他',
        value: 'other',
      },
    ],
    onFilter: (value, record) => record.type === value,
    render: (text) => {
      if (text === AutoBanType.HRNA_UUID_OS) {
        return '高风险新账号-系统关联';
      } else if (text === AutoBanType.HRNA_UUID_HARDWARE) {
        return '高风险新账号-硬件关联';
      } else if (text === AutoBanType.PROCESS_KEYWORD) {
        return '进程信息-关键词';
      } else if (text === AutoBanType.MAP_REPORT) {
        return '地图上报';
      } else if (text === AutoBanType.PROCESS_V2) {
        return '进程扫描V2';
      } else {
        return '其他';
      }
    },
  },
  {
    width: 280,
    title: 'UserId',
    dataIndex: 'user_id',
    key: 'user_id',
    render: (_, record) => {
      return (
        <div>
          <div>{record.user_id}</div>
          <div>{record.nick}</div>
        </div>
      );
    },
  },
  {
    width: 280,
    title: '注册时间',
    dataIndex: 'registerTime',
    key: 'registerTime',
    render: (value) => {
      const regtime = DateTime.fromISO(value);
      // 计算到现在的天数
      const days = DateTime.now().diff(regtime, 'days').days.toFixed(1);
      return (
        <div>
          {regtime.toFormat('yyyy-MM-dd HH:mm:ss')} ( {days} 天)
        </div>
      );
    },
  },
  {
    width: 120,
    title: '账号状态',
    dataIndex: 'status',
    key: 'status',
    render: (value) => {
      return renderAccountStatus(value);
    },
  },
  {
    title: '拓展数据',
    dataIndex: 'ext',
    key: 'ext',
    render: (_, record) => {
      if (isHrnaLog(record.type) && record.hrnaData) {
        return (
          <div>
            <div>关联 UUID: {record.hrnaData.uuid}</div>
            <div>关联封禁人数: {record.hrnaData.related_banned_count}</div>
            <div>关联 user_id</div>
            <div>
              {record.hrnaData.related_banned_user_ids.map((userId) => {
                const userInfo = record.relatedUsers?.find((user) => user.userId === userId);
                const status = userInfo?.status ?? -1;
                return (
                  <div key={userId}>
                    <span>{userId}</span>
                    {userInfo?.nick ? <span> / {userInfo.nick} / </span> : <span> / </span>}
                    <span style={status === 0 ? { color: 'red', fontWeight: 'bold' } : {}}> {getAccountStatusText(status)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      if (isHrnaLog(record.type)) {
        const value = record.uuidList;
        return (
          <div>
            <div>历史使用 UUID</div>
            <div>
              {value.map((uuid) => (
                <div key={uuid}>{uuid}</div>
              ))}
            </div>
          </div>
        );
      }

      if (record.type === AutoBanType.PROCESS_KEYWORD) {
        const list: PDItem[] = record.extData || [];

        let keywordList: string[] = [];

        const configPD = store.get().configPD;
        if (configPD) {
          keywordList = JSON.parse(configPD.config).keyword_list;
        }

        // 如果有命中的，就只展示一条，如果没有，就都展示
        const matchMark: Record<number, boolean> = {};

        const listDom = list.map((item, index) => {
          // 高亮关键词
          let highlightedProcessData = item.ProcessData;
          // 预处理，包含 % 的切分成多个
          const _klist = keywordList.flatMap((k) => k.split('%'));
          for (const keyword of _klist) {
            const regex = new RegExp(`(${escapeRegExp(keyword)})`, 'gi');
            if (regex.test(highlightedProcessData)) {
              matchMark[index] = true;
            }
            highlightedProcessData = highlightedProcessData.replace(regex, '<span style="background-color: red;">$1</span>');
          }

          return <div key={item.ID} dangerouslySetInnerHTML={{ __html: highlightedProcessData }}></div>;
        });

        if (list.length > 0 && Object.keys(matchMark).length > 0) {
          // 只展示第一条命中的
          const firstMatchIndex = Math.min(...Object.keys(matchMark).map((k) => parseInt(k)));
          return <div>{listDom[firstMatchIndex]}</div>;
        }

        return <div>{listDom}</div>;
      }

      if (record.type === AutoBanType.PROCESS_V2) {
        const data = JSON.parse(record.data) as ProcessReportV2Data;
        const list: PDItem[] = record.extData || [];

        return (
          <div>
            <div>命中类型 {JSON.stringify(data.categories.map((cat) => cat.name))}</div>
            {list.map((item) => (
              <div key={item.ID}>{item.ProcessData}</div>
            ))}
          </div>
        );
      }

      if (record.type === AutoBanType.MAP_REPORT) {
        return <div>{record.data}</div>;
      }

      return <div>无</div>;
    },
  },
];

type QueryForm = {
  start?: Dayjs;
  end?: Dayjs;
};

interface LogItemEx {
  data: string;
  date: string;
  id: number;
  type: string;
  user_id: string;

  nick: string;
  registerTime: string;
  status: number;

  uuidList: string[];
  hrnaData?: HrnaAutoBanData;
  relatedUsers?: RelatedUserInfo[];
  extData?: any;
}

interface State {
  serverConfig: {
    enable: boolean;
  };
  loading: boolean;
  logList: LogItemEx[];

  /**
   * 加载这个是为了能高亮进程数据
   */
  configPD: ConfigItem | null;
}

const DefaultData: State = {
  serverConfig: {
    enable: false,
  },
  loading: false,
  logList: [] as LogItemEx[],

  configPD: null,
};

const store = atom(DefaultData);

async function loadLog(query: QueryForm) {
  store.set({ ...store.get(), loading: true });

  const logList = await Scheduler.autoBanLog({
    start: query.start!.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    end: query.end!.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  });

  if (logList.length === 0) {
    const serverConfig = await Scheduler.configGet();

    store.set({
      ...store.get(),
      serverConfig,
      loading: false,
      logList: [],
    });
    return;
  }

  const logListEx: LogItemEx[] = [];
  const userIdSet = new Set<string>();
  const oldHrnaUserIdSet = new Set<string>();
  const relatedUserIdSet = new Set<string>();
  for (const logItem of logList) {
    userIdSet.add(logItem.user_id);

    const hrnaData = isHrnaLog(logItem.type) ? parseHrnaAutoBanData(logItem.data) : null;
    if (hrnaData) {
      for (const userId of hrnaData.related_banned_user_ids) {
        relatedUserIdSet.add(userId);
      }
    } else if (isHrnaLog(logItem.type)) {
      oldHrnaUserIdSet.add(logItem.user_id);
    }

    logListEx.push({
      nick: '',
      registerTime: '',
      status: -1,
      uuidList: [],
      hrnaData: hrnaData || undefined,
      relatedUsers: [],
      ...logItem,
    });
  }

  // 获取用户信息
  const userIdList = Array.from(new Set([...userIdSet, ...relatedUserIdSet]));
  const userInfoList = userIdList.length > 0 ? await User.infoList(userIdList) : [];
  const userInfoMap: Record<string, (typeof userInfoList)[0]> = {};
  for (const userInfo of userInfoList) {
    userInfoMap[userInfo.UserId] = userInfo;
  }

  // 旧 HRNA 日志没有记录命中 UUID，继续按原逻辑从登录日志反查。
  const userUUIDMap = new Map<string, string[]>();
  for (const userId of oldHrnaUserIdSet) {
    const osLogList = await User.OSInfoLog(userId);

    const hwSet = new Set<string>();
    const osSet = new Set<string>();
    for (const osLog of osLogList) {
      hwSet.add(osLog.data.uuid_hardware);
      osSet.add(osLog.data.uuid_os);
    }
    userUUIDMap.set(`${userId}-hw`, Array.from(hwSet));
    userUUIDMap.set(`${userId}-os`, Array.from(osSet));
  }

  // 合并用户信息
  for (const logItem of logListEx) {
    const userInfo = userInfoMap[logItem.user_id];
    if (userInfo) {
      logItem.nick = userInfo.NickName;
      logItem.registerTime = userInfo.UserRegtime;
      logItem.status = userInfo.Status;
    }

    if (isHrnaLog(logItem.type) && logItem.hrnaData) {
      logItem.relatedUsers = logItem.hrnaData.related_banned_user_ids.map((userId) => {
        const relatedUserInfo = userInfoMap[userId];
        return {
          userId,
          nick: relatedUserInfo?.NickName || '',
          status: relatedUserInfo?.Status ?? -1,
        };
      });
    } else if (isHrnaLog(logItem.type)) {
      const uuidKey = logItem.type === AutoBanType.HRNA_UUID_HARDWARE ? `${logItem.user_id}-hw` : `${logItem.user_id}-os`;
      logItem.uuidList = (userUUIDMap.get(uuidKey) || []).slice(0, 5); // 只取前 5 个
    }
    if (logItem.type === AutoBanType.PROCESS_KEYWORD) {
      // 加载这次数据
      // {\"matched_process_log_ids\":[136628742,136628744]}
      const extData: { matched_process_log_ids: number[] } = JSON.parse(logItem.data);

      const list = await Scheduler.processDataList(extData.matched_process_log_ids.slice(0, 3));
      logItem.extData = list;
    } else if (logItem.type === AutoBanType.PROCESS_V2) {
      const data: ProcessReportV2Data = JSON.parse(logItem.data);

      const ids = data.categories.flatMap((cat) => cat.matched_log_ids);
      const list = await Scheduler.processDataList(Array.from(new Set(ids)).slice(0, 1));
      logItem.extData = list;
    }
  }

  const serverConfig = await Scheduler.configGet();

  store.set({
    loading: false,
    logList: logListEx,
    serverConfig,

    configPD: await Config.getConfig('process_data_task_config'),
  });
}

function clearState() {
  store.set(DefaultData);
}

let dayStart = dayjs().startOf('day');
let dayEnd = dayjs().endOf('day');

// 展示信用分的历史，当前值，和修改按钮

export default function AutoBanStatus() {
  useEffect(() => {
    loadLog({ start: dayStart, end: dayEnd });

    return () => {
      clearState();
    };
  }, []);

  const state = useStore(store);

  const hrnaOSCount = countUniqueUsers(state.logList, (log) => log.type === AutoBanType.HRNA_UUID_OS);
  const hrnaOSBanCount = countUniqueUsers(state.logList, (log) => log.type === AutoBanType.HRNA_UUID_OS && log.status === 0);

  const hrnaHardwareCount = countUniqueUsers(state.logList, (log) => log.type === AutoBanType.HRNA_UUID_HARDWARE);
  const hrnaHardwareBanCount = countUniqueUsers(state.logList, (log) => log.type === AutoBanType.HRNA_UUID_HARDWARE && log.status === 0);

  const processKeywordCount = countUniqueUsers(state.logList, (log) => log.type === AutoBanType.PROCESS_KEYWORD);
  const processKeywordBanCount = countUniqueUsers(state.logList, (log) => log.type === AutoBanType.PROCESS_KEYWORD && log.status === 0);

  const processDataV2Count = countUniqueUsers(state.logList, (log) => log.type === AutoBanType.PROCESS_V2);
  const processDataV2BanCount = countUniqueUsers(state.logList, (log) => log.type === AutoBanType.PROCESS_V2 && log.status === 0);

  const mapReportCount = countUniqueUsers(state.logList, (log) => log.type === AutoBanType.MAP_REPORT);

  const borderedItems: DescriptionsProps['items'] = [
    {
      key: 'hrna',
      label: '高风险新账号扫描',
      children: (
        <div style={{ color: state.serverConfig.enable ? 'green' : 'red', fontWeight: 'bold' }}>
          {state.serverConfig.enable ? '运行中' : '未启用'}
        </div>
      ),
    },
    {
      key: 'action',
      label: '操作',
      children: (
        <div>
          <Flex gap="small" wrap>
            <Button
              color="cyan"
              variant="solid"
              onClick={async () => {
                await Scheduler.configSet({ enable: true, feishu: true });
                loadLog({ start: dayStart, end: dayEnd });
              }}
            >
              启用扫描
            </Button>
            <Button
              color="danger"
              variant="solid"
              onClick={async () => {
                await Scheduler.configSet({ enable: false, feishu: true });
                loadLog({ start: dayStart, end: dayEnd });
              }}
            >
              关闭扫描
            </Button>
          </Flex>
        </div>
      ),
    },
    {
      key: 'other',
      label: '其他',
      children: (
        <div>
          <Flex gap="small" wrap>
            <Button
              variant="solid"
              color="orange"
              onClick={async () => {
                const allUserIds = Array.from(new Set(state.logList.map((log) => log.user_id))).join('\n');
                await navigator.clipboard.writeText(allUserIds);
                message.success('已复制全部 userId 到剪贴板');
              }}
            >
              复制全部 userId
            </Button>
          </Flex>
        </div>
      ),
    },
    {
      key: 'count_hardware',
      label: '处理硬件关联',
      children: (
        <div>
          {hrnaHardwareCount} ({hrnaHardwareBanCount}) 人
        </div>
      ),
    },
    {
      key: 'count_os',
      label: '处理系统关联',
      children: (
        <div>
          {hrnaOSCount} ({hrnaOSBanCount}) 人
        </div>
      ),
    },
    {
      key: 'count_pd',
      label: '处理进程关联',
      children: (
        <div>
          {processKeywordCount} ({processKeywordBanCount}) 人
        </div>
      ),
    },
    {
      key: 'count_pd_v2',
      label: '处理进程关联 V2',
      children: (
        <div>
          {processDataV2Count} ({processDataV2BanCount}) 人
        </div>
      ),
    },
    {
      key: 'count_mr',
      label: '处理地图上报',
      children: <div>{mapReportCount} 人</div>,
    },
  ];

  return (
    <div>
      <Row>
        <Col span={8}>
          <Form<QueryForm>
            name="list"
            layout="horizontal"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            style={{ maxWidth: 600 }}
            initialValues={{ start: dayStart, end: dayEnd }}
            onValuesChange={(changedValues, allValues) => {
              if (allValues.start) {
                dayStart = allValues.start;
              }
              if (allValues.end) {
                dayEnd = allValues.end;
              }
            }}
            onFinish={(values: QueryForm) => {
              if (values.start && values.end) {
                loadLog(values);
              }
            }}
            autoComplete="off"
          >
            <Form.Item<QueryForm> label="开始日期" name="start">
              <DatePicker disabled={state.loading} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item<QueryForm> label="结束日期" name="end">
              <DatePicker disabled={state.loading} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label={null}>
              <Button type="primary" htmlType="submit" loading={state.loading}>
                查询
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <div style={{ margin: '24px 0' }}>
        <Descriptions bordered items={borderedItems}></Descriptions>
      </div>
      <div>
        <Table rowKey="id" dataSource={state.logList} columns={columns} pagination={false} />
      </div>
    </div>
  );
}
